import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, LayersControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet"; // Import the Leaflet library for L.geoJSON
import { MapHomeControl, MapInfoControl } from "../../common/MapControls/MapControls";

const Population = () => {
  const [boundaryData, setBoundaryData] = useState(null); // Country (ADM0)
  const [municipalityData, setMunicipalityData] = useState(null); // Municipality (ADM1)
  const [postAdminData, setPostAdminData] = useState(null); // Post Admin (ADM2)
  const [sucoData, setSucoData] = useState(null); // Suco (ADM3)
  const [mapBounds, setMapBounds] = useState(null); // New state for map bounds

  // Fetch all GeoJSON data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [adm0Res, adm1Res, adm2Res, adm3Res] = await Promise.all([
          fetch("/DATA/OSMbased_adm0_boundary.json"),
          fetch("/DATA/OSMbased_adm1_boundary.json"),
          fetch("/DATA/OSMbased_adm2_boundary.json"),
          fetch("/DATA/OSMbased_adm3_boundary.json"),
        ]);

        const [adm0Data, adm1Data, adm2Data, adm3Data] = await Promise.all([
          adm0Res.json(),
          adm1Res.json(),
          adm2Res.json(),
          adm3Res.json(),
        ]);

        setBoundaryData(adm0Data);
        setMunicipalityData(adm1Data);
        setPostAdminData(adm2Data);
        setSucoData(adm3Data);

        // Calculate and set map bounds from the country boundary data
        if (adm0Data && adm0Data.features && adm0Data.features.length > 0) {
          const geoJsonLayer = L.geoJSON(adm0Data);
          setMapBounds(geoJsonLayer.getBounds());
        }
      } catch (error) {
        console.error("Error loading GeoJSON data:", error);
      }
    };

    fetchData();
  }, []);

  // Function to aggregate data from a lower administrative level
  const aggregateData = (lowerLevelData, higherLevelGeoJSON, higherLevelKey, lowerLevelKey) => {
    if (!lowerLevelData || !higherLevelGeoJSON) return higherLevelGeoJSON;

    const aggregatedProperties = {};

    lowerLevelData.features.forEach(lowerFeature => {
      const higherLevelIdentifier = lowerFeature.properties[higherLevelKey];
      if (higherLevelIdentifier) {
        if (!aggregatedProperties[higherLevelIdentifier]) {
          aggregatedProperties[higherLevelIdentifier] = {
            Population: 0,
            Male: 0,
            Female: 0,
            Household: 0,
            HH_Urban: 0,
            HH_Rural: 0,
            Area_sqkm: 0
          };
        }
        aggregatedProperties[higherLevelIdentifier].Population += (lowerFeature.properties.Population || 0);
        aggregatedProperties[higherLevelIdentifier].Male += (lowerFeature.properties.Male || 0);
        aggregatedProperties[higherLevelIdentifier].Female += (lowerFeature.properties.Female || 0);
        aggregatedProperties[higherLevelIdentifier].Household += (lowerFeature.properties.Household || 0);
        aggregatedProperties[higherLevelIdentifier].HH_Urban += (lowerFeature.properties.HH_Urban || 0);
        aggregatedProperties[higherLevelIdentifier].HH_Rural += (lowerFeature.properties.HH_Rural || 0);
        aggregatedProperties[higherLevelIdentifier].Area_sqkm += (lowerFeature.properties.Area_sqkm || 0);
      }
    });

    const updatedHigherLevelGeoJSON = {
      ...higherLevelGeoJSON,
      features: higherLevelGeoJSON.features.map(higherFeature => {
        const identifier = higherFeature.properties[higherLevelKey];
        return {
          ...higherFeature,
          properties: {
            ...higherFeature.properties,
            ...aggregatedProperties[identifier],
          },
        };
      }),
    };
    return updatedHigherLevelGeoJSON;
  };

  // Render GeoJSON layers with dynamic popups and tooltips
  const renderGeoJSONLayer = (data, name, color, initialFillOpacity, currentLevelKey, lowerLevelDataForAggregation = null, lowerLevelKeyForAggregation = null, higherLevelKeyForAggregation = null, isChecked = false) => {
    if (!data) return null;

    let displayData = data;

    // Perform aggregation if lowerLevelDataForAggregation is provided
    if (lowerLevelDataForAggregation && lowerLevelKeyForAggregation && higherLevelKeyForAggregation) {
      displayData = aggregateData(lowerLevelDataForAggregation, data, higherLevelKeyForAggregation, lowerLevelKeyForAggregation);
    }

    return (
      <LayersControl.BaseLayer name={name} checked={isChecked}>
        <GeoJSON
          data={displayData}
          style={{
            color: color,
            weight: 2,
            fillColor: color,
            fillOpacity: initialFillOpacity,
          }}
          onEachFeature={(feature, layer) => {
            if (feature.properties) {
              const properties = { ...feature.properties };

              // Customize property names for display
              if (properties.ADM0) {
                properties.Country = properties.ADM0;
                delete properties.ADM0;
              }
              if (properties.ADM1) {
                properties.Municipality = properties.ADM1;
                delete properties.ADM1;
              }
              if (properties.ADM2) {
                properties.Post_Admin = properties.ADM2;
                delete properties.ADM2;
              }
              if (properties.ADM3) {
                properties.Suco = properties.ADM3;
                delete properties.ADM3;
              }
              if (properties.Population) {
                properties.Population = properties.Population.toLocaleString();
              }
              if (properties.Male) {
                properties.Male = properties.Male.toLocaleString();
              }
              if (properties.Female) {
                properties.Female = properties.Female.toLocaleString();
              }
              if (properties.Household) {
                properties.Household = properties.Household.toLocaleString();
              }
              if (properties.HH_Urban) {
                properties.HH_Urban = properties.HH_Urban.toLocaleString();
              }
              if (properties.HH_Rural) {
                properties.HH_Rural = properties.HH_Rural.toLocaleString();
              }
              if (properties.Area_sqkm) {
                properties.Area_sqkm = properties.Area_sqkm.toFixed(2);
              }

              const relevantProperties = [
                "Country", "Municipality", "Post_Admin", "Suco",
                "Population", "Male", "Female", "Household", "HH_Urban", "HH_Rural", "Area_sqkm"
              ];

              const popupContent = `
                <b>${name} Details</b><br>
                ${Object.entries(properties)
                  .filter(([key]) => relevantProperties.includes(key))
                  .map(([key, value]) => `<b>${key}</b>: ${value}`)
                  .join("<br>")}
              `;

              layer.on("mouseover", (e) => {
                layer.bindTooltip(popupContent, { sticky: true }).openTooltip(e.latlng);
                layer.setStyle({
                  color: "darkred",
                  weight: 3,
                  fillOpacity: 0.6,
                });
              });

              layer.on("mouseout", () => {
                layer.closeTooltip();
                layer.setStyle({
                  color: color,
                  weight: 2,
                  fillOpacity: initialFillOpacity,
                });
              });

              layer.on("click", (e) => {
                layer.bindPopup(popupContent).openPopup(e.latlng);
              });
            }
          }}
        />
      </LayersControl.BaseLayer>
    );
  };

  return (
    <div className="mapcontainerall">
      {mapBounds && ( // Conditionally render the map only when bounds are available
        <MapContainer
          bounds={mapBounds} // Use the calculated bounds
          style={{ height: "100%", width: "100%" }}
        >
          <MapHomeControl />
          <MapInfoControl text="Population distribution across Timor-Leste used for fire risk exposure assessment." />
          {/* Base tile layer */}
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          {/* Layer Control */}
          <LayersControl position="topright">
            {/* Country Layer (ADM0) */}
            {renderGeoJSONLayer(
              boundaryData,
              "Country",
              "blue",
              0,
              "ADM0",
              municipalityData,
              "ADM0",
              "ADM0",
              false
            )}

            {/* Municipality Layer (ADM1) */}
            {renderGeoJSONLayer(
              municipalityData,
              "Municipality",
              "green",
              0.3,
              "ADM1",
              sucoData,
              "ADM1",
              "ADM1",
              true
            )}

            {/* Post Admin Layer (ADM2) */}
            {renderGeoJSONLayer(
              postAdminData,
              "Post Admin",
              "purple",
              0.3,
              "ADM2",
              sucoData,
              "ADM2",
              "ADM2"
            )}

            {/* Suco Layer (ADM3) */}
            {renderGeoJSONLayer(sucoData, "Suco", "black", 0.3, "ADM3")}
          </LayersControl>
        </MapContainer>
      )}
    </div>
  );
};

export default Population;